import React, {
  useState,
  useEffect,
  useContext,
  memo,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { withRouter } from "react-router";
import { StoreContext } from "../Stores";
import { TYPES } from "../Reducers/menuReducer.js";
import { Spinner, Container, Navbar, Col, Alert } from "react-bootstrap";
import { HashLink as Link } from "react-router-hash-link";
import { ReactComponent as BookMarkListIcon } from "../bookmark-list.svg";
import { ReactComponent as ArrowIcon } from "../arrow-icon.svg";
import { sortMenus, formatMenuData } from "../Services/FormatMenu";
import DisplayMenu from "./DisplayMenu";

const loadMenuJSONData = (menuUrl = "menu.json") =>
  new Promise((resolve, reject) => {
    try {
      fetch(`${menuUrl}?nocache=${new Date().getTime()}`)
        .then((r) => r.json())
        .then((data) => resolve(data))
        .catch((err) => {
          console.log(err);
          reject("File parsing error, no rows found!");
        });
    } catch (e) {
      reject("File parsing error, no rows found!");
    }
  });

const Menus = memo(
  ({ menusData, match, displayWCAGLogo, bookmarksEnabled, loadSectionOnly }) => {
    const contextValue = useContext(StoreContext);
    const [loading, setLoading] = useState(false);
    const [scrollMenu, setScrollMenu] = useState(false);
    const [menuData, setMenuData] = useState({
      tocList: [],
      fullJson: [],
      listMenus: [],
      headerFour: null,
      headerOne: null,
      headerThree: null,
      headerTwo: null,
      normalText: null,
      subtext: null,
      selectedMenu: "",
      error: null,
      currencySymbol: "",
      menuLayout: "DEFAULT",
    });
    const menuId = match.params.menuId;
    const menuItemIndex = match.params.menuItemIndex;
    const dispatchCallback = useCallback(contextValue.dispatch, []);
    const storeMenu = useMemo(() => contextValue.state.menu, [
      contextValue.state.menu,
    ]);
    const navMenuScrollerEle = useRef(null);
    const navMenuEle = useRef(null);

    useEffect(() => {
      const { menus, menusPosition } = menusData;

      /**
       * Do load single menu or multiple menus here
       */
      const loadMenus = async (menuId) => {
        /**
         * If don't have "menus" property => do fetch old menu
         */
        if (!menus) {
          const oldMenuStoreData = storeMenu["menu.json"];
          if (oldMenuStoreData) {
            const formattedOldMenu = formatMenuData(oldMenuStoreData);

            setMenuData((prevProps) => ({
              ...prevProps,
              currencySymbol: menusData.currencySymbol,
              tocList: formattedOldMenu,
              fullJson: oldMenuStoreData, // keep a copy of the initial flat data list for easy search
              listMenus: [],
              selectedMenu: "",
              error: "",
            }));

            return;
          }

          // If don't exist the old menu in store yet, do fetch
          try {
            setLoading(true);
            const oldMenuResponse = await loadMenuJSONData("menu.json");

            dispatchCallback({
              type: TYPES.SAVE_MENU,
              menu: { jsonPath: "menu.json", jsonData: oldMenuResponse },
            });
          } catch (error) {
            setMenuData((prevProps) => ({
              ...prevProps,
              currencySymbol: menusData.currencySymbol,
              error: "Not found menu! " + error,
            }));
          } finally {
            setLoading(false);
          }

          return;
        }

        /**
         * If have "menus" property
         * Do fetch multiple menus here
         */

        // Get the sorted of menus
        const sortedMenus = sortMenus(menus, menusPosition);

        /**
         * If have only one menu in list:
         * - Get the first menu in list
         * - Checking if menu already existed in menu store.
         *    - If exist, do load menu data from store and return
         *    - If not exist, do fetch menu data base on the menu's jsonPath. Save fetched menu to menu store
         */
        if (sortedMenus.length === 1) {
          // Get first menu in list
          const menuSettingInfo = sortedMenus[0];
          const {
            jsonPath,
            id,
            headerOne,
            headerTwo,
            headerThree,
            headerFour,
            normalText,
            subtext,
            menuLayout,
          } = menuSettingInfo;

          // Try to load menu from store
          const menuStoreData = storeMenu[jsonPath];
          if (menuStoreData) {
            // Load menu data from store, format then set data to menu
            const formattedMenu = formatMenuData(menuStoreData);

            setMenuData((prevProps) => ({
              ...prevProps,
              currencySymbol: menusData.currencySymbol,
              headerOne,
              headerTwo,
              headerThree,
              headerFour,
              normalText,
              subtext,
              menuLayout,
              tocList: formattedMenu,
              fullJson: menuStoreData, // keep a copy of the initial flat data list for easy search
              listMenus: sortedMenus,
              selectedMenu: id,
              error: "",
            }));

            return;
          }

          try {
            // If menu doesn't exists in menu store => do fetch menu data base on jsonPath
            setLoading(true);

            // Fetch menu data from store, format then set data to menu
            const menuJsonData = await loadMenuJSONData(jsonPath);

            // Save fetched menu to menu store
            dispatchCallback({
              type: TYPES.SAVE_MENU,
              menu: { jsonPath, jsonData: menuJsonData },
            });
          } catch (error) {
            setMenuData((prevProps) => ({
              ...prevProps,
              currencySymbol: menusData.currencySymbol,
              error: "Not found menu! " + error,
            }));
          } finally {
            setLoading(false);
          }

          return;
        }

        /**
         * If have multiple menus in list:
         * - Get the selected menu in list menus (first menu as default)
         * - Checking if menu already existed in menu store. If exist => load menu data from store and return
         * - Do fetch menu data base on the menu's jsonPath. Save fetched menu to menu store
         */

        // Get first menu as default
        let menuSettingInfo = sortedMenus[0];

        // Get right menu on list base on menuId
        if (menuId && menus?.length) {
          menuSettingInfo = menus.find((menu) => menu.id === menuId);
        }

        // If don't exist. throw error
        if (!menuSettingInfo) {
          setMenuData((prevProps) => ({
            ...prevProps,
            currencySymbol: menusData.currencySymbol,
            error: "Not found menu!",
          }));

          return;
        }

        const {
          id,
          jsonPath,
          headerOne,
          headerTwo,
          headerThree,
          headerFour,
          normalText,
          subtext,
          menuLayout,
        } = menuSettingInfo;

        // Try to load menu from store
        const menuStoreData = storeMenu[jsonPath];
        if (menuStoreData) {
          // Load menu data from store, format then set data to menu
          const formattedData = formatMenuData(menuStoreData);

          setMenuData((prevProps) => ({
            ...prevProps,
            currencySymbol: menusData.currencySymbol,
            headerOne,
            headerTwo,
            headerThree,
            headerFour,
            normalText,
            subtext,
            menuLayout: menuLayout ? menuLayout : "DEFAULT",
            tocList: formattedData,
            fullJson: menuStoreData, // keep a copy of the initial flat data list for easy search
            listMenus: sortedMenus,
            selectedMenu: id,
            error: "",
          }));

          return;
        }

        try {
          // If menu doesn't exists in menu store => do fetch menu data base on jsonPath
          setLoading(true);

          const menuJsonData = await loadMenuJSONData(jsonPath);

          dispatchCallback({
            type: TYPES.SAVE_MENU,
            menu: { jsonPath, jsonData: menuJsonData },
          });
        } catch (error) {
          setMenuData((prevProps) => ({
            ...prevProps,
            currencySymbol: menusData.currencySymbol,
            error: "Not found menu! " + error,
          }));

          return;
        } finally {
          setLoading(false);
        }
      };

      // Do load menu base on the menu's id
      loadMenus(menuId);
    }, [menusData, menuId, dispatchCallback, storeMenu]);

    /**
     * Effect used to toggle scroll menus in menu list
     * if Restaurant have many menus
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
      if (navMenuScrollerEle?.current && navMenuEle?.current) {
        if (
          navMenuEle.current.clientWidth >
          navMenuScrollerEle.current.clientWidth
        ) {
          setScrollMenu(true);
        }
      }
    });

    const handleScrollLeft = () => {
      navMenuScrollerEle.current.scrollLeft -= 50;
    };

    const handleScrollRight = () => {
      navMenuScrollerEle.current.scrollLeft += 50;
    };

    return (
      <>
        {loading && (
          <div className="spinner-backdrop d-flex justify-content-center align-items-center">
            <Spinner
              as="div"
              animation="border"
              size="lg"
              role="isProcessing"
              aria-hidden="true"
            />
          </div>
        )}
        {menuData.listMenus.length > 1 &&
          (menuItemIndex === null || menuItemIndex === undefined) && (
            <Container>
              {
                <Navbar
                  expand="lg"
                  sticky="top"
                  bg="white"
                  className="px-0"
                  aria-label="Menu list"
                  role="navigation"
                  style={{ borderBottom: "1px solid rgb(231, 231, 231)" }}
                >
                  <Col xs={10} sm={11} className="p-0 menus-name-wrapper">
                    {scrollMenu && (
                      <div className="scroller-controller">
                        <div
                          className="scroll-btn left"
                          role="button"
                          onClick={handleScrollLeft}
                        >
                          <ArrowIcon />
                        </div>
                        <div
                          className="scroll-btn right"
                          role="button"
                          onClick={handleScrollRight}
                        >
                          <ArrowIcon />
                        </div>
                      </div>
                    )}
                    <div
                      style={{ width: scrollMenu ? "90%" : "" }}
                      ref={navMenuScrollerEle}
                      className="nav-scroller py-1"
                    >
                      <nav className="nav" ref={navMenuEle}>
                        {menuData.listMenus.map((menu) => (
                          <Link
                            key={menu.id}
                            className={`p-2 ${
                              menu.id === menuData.selectedMenu
                                ? "font-weight-bold active"
                                : "text-muted"
                            }`}
                            to={`/${menu.id}`}
                            aria-label={`Link to ${menu.name}`}
                          >
                            {menu.name}
                          </Link>
                        ))}
                      </nav>
                    </div>
                  </Col>
                  <Col xs={2} sm={1} className="d-flex justify-content-end p-0">
                    {bookmarksEnabled && (
                      <Link to="/bookmark">
                        <BookMarkListIcon />
                      </Link>
                    )}
                  </Col>
                </Navbar>
              }
            </Container>
          )}
        {menuData.error ? (
          <Alert variant="danger">{menuData.error}</Alert>
        ) : (
          <>
            <DisplayMenu
              {...menuData}
              menuItemIndex={menuItemIndex}
              bookmarksEnabled={bookmarksEnabled}
              loadSectionOnly={loadSectionOnly}
            />
            {/* "menuLayout" can be "null" or "undefined" (old menu) */}
            {displayWCAGLogo && menuData.menuLayout !== "WITH_IMAGES" ? (
              <div className="container d-flex justify-content-end pb-3">
                <a
                  href="https://www.w3.org/WAI/WCAG2A-Conformance"
                  title="Explanation of WCAG 2 Level A Conformance"
                >
                  <img
                    height="32"
                    width="88"
                    src="https://www.w3.org/WAI/WCAG21/wcag2.1A-v"
                    alt="Level A conformance, W3C WAI Web Content Accessibility Guidelines 2.1"
                  />
                </a>
              </div>
            ) : null}
          </>
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.menusData === nextProps.menusData &&
      prevProps.match.params.menuId === nextProps.match.params.menuId &&
      prevProps.displayWCAGLogo === nextProps.displayWCAGLogo &&
      prevProps.match.params.menuItemIndex ===
        nextProps.match.params.menuItemIndex
    );
  }
);

export default withRouter(Menus);
